import React, { useEffect, useCallback, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import {
    Users,
    ShoppingBag,
    TrendingDown,
    Bot,
    QrCode,
    ChevronLeft,
    ChevronRight
} from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '@/lib/utils';

const MODULES = [
    {
        title: "Gestión de Membresías",
        description: "Control total sobre tus socios, pagos y perfiles en una interfaz profesional y rápida.",
        image: "/landing/memberships.png",
        icon: Users,
        color: "from-blue-600 to-indigo-600"
    },
    {
        title: "Punto de Venta (POS)",
        description: "Venda suplementos, bebidas y equipo directamente desde el mostrador con inventario sincronizado.",
        image: "/landing/pos.png",
        icon: ShoppingBag,
        color: "from-orange-600 to-amber-500"
    },
    {
        title: "Control de Gastos",
        description: "Monitoree la salud financiera de su negocio. Registre compras, servicios y nóminas fácilmente.",
        image: "/landing/expenses.png",
        icon: TrendingDown,
        color: "from-emerald-600 to-teal-500"
    },
    {
        title: "Asistente con IA",
        description: "Atención automatizada por WhatsApp. Responda dudas y gestione citas 24/7 sin esfuerzo manual.",
        image: "/landing/whatsapp.png",
        icon: Bot,
        color: "from-purple-600 to-pink-500"
    },
    {
        title: "Accesos con QR",
        description: "Seguridad y agilidad. Permita ingresos mediante escaneo de códigos QR en tiempo real.",
        image: "/landing/access.png",
        icon: QrCode,
        color: "from-red-600 to-orange-600"
    }
];

export function ModuleCarousel() {
    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, duration: 30 });
    const [selectedIndex, setSelectedIndex] = useState(0);

    const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
    const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

    const onSelect = useCallback(() => {
        if (!emblaApi) return;
        setSelectedIndex(emblaApi.selectedScrollSnap());
    }, [emblaApi, setSelectedIndex]);

    useEffect(() => {
        if (!emblaApi) return;
        onSelect();
        emblaApi.on('select', onSelect);
        emblaApi.on('reInit', onSelect);

        const intervalId = setInterval(() => {
            emblaApi.scrollNext();
        }, 5000);

        return () => {
            emblaApi.off('select', onSelect);
            clearInterval(intervalId);
        };
    }, [emblaApi, onSelect]);

    return (
        <div className="relative group">
            <div className="overflow-hidden rounded-[2rem] lg:rounded-[3rem] shadow-2xl border border-white/10" ref={emblaRef}>
                <div className="flex">
                    {MODULES.map((module, index) => (
                        <div key={index} className="flex-[0_0_100%] min-w-0 relative h-[500px] md:h-[650px]">
                            {/* Image with overlay gradient */}
                            <div className="absolute inset-0 transition-transform duration-700 hover:scale-105">
                                <img
                                    src={module.image}
                                    alt={module.title}
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent" />
                            </div>

                            {/* Content */}
                            <div className="absolute bottom-0 left-0 p-8 md:p-16 w-full md:max-w-3xl animate-in fade-in slide-in-from-bottom-5 duration-700">
                                <div className={cn(
                                    "inline-flex p-3 rounded-2xl bg-gradient-to-br text-white shadow-lg mb-6",
                                    module.color
                                )}>
                                    <module.icon className="w-8 h-8" />
                                </div>
                                <h3 className="text-3xl md:text-5xl font-black text-white mb-4 tracking-tighter uppercase italic">
                                    {module.title}
                                </h3>
                                <p className="text-lg md:text-xl text-slate-200 font-medium leading-relaxed max-w-xl">
                                    {module.description}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Navigation Controls */}
            <button
                onClick={scrollPrev}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white opacity-0 group-hover:opacity-100 transition-opacity z-20 hover:bg-white/20"
            >
                <ChevronLeft className="w-6 h-6" />
            </button>

            <button
                onClick={scrollNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white opacity-0 group-hover:opacity-100 transition-opacity z-20 hover:bg-white/20"
            >
                <ChevronRight className="w-6 h-6" />
            </button>

            {/* Progress Indicators */}
            <div className="absolute bottom-8 right-8 z-20 flex gap-2">
                {MODULES.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => emblaApi?.scrollTo(index)}
                        className={cn(
                            "h-2 transition-all duration-300 rounded-full",
                            selectedIndex === index ? "w-12 bg-white" : "w-2 bg-white/30 hover:bg-white/50"
                        )}
                    />
                ))}
            </div>
        </div>
    );
}
